/**
 * <search-overlay> - predictive search. Foerste forbruger af debounce + abortable
 * fra @theme/section-render. Progressive enhancement: uden JS poster header-formen
 * til /search (search.json fuld side); med JS aabnes et modal-overlay med live-
 * resultater pr. tastetryk.
 *
 * Mekanik (genbruger den delte util, intet duplikeres):
 *  - debounce(300) paa keystroke + abortable() saa et nyt tastetryk afbryder det
 *    forrige in-flight kald (latest-wins).
 *  - fetchSection('predictive-search', { url: .../search/suggest, params, signal })
 *    henter den server-renderede predictive-search-sektion; swapInner indsaetter
 *    resultaterne + baerer den subsettede <style> med.
 *  - Penge server-ejet: priser kommer renderet retur via | money, aldrig client-side.
 *  - Tom tilstand: fetchSection('search-default') ÉN gang ved foerste aabning
 *    (populaere soegetermer + bestsellers, begge koen i samme svar - CSS vaelger
 *    via html[data-gender]). Vises kun mens feltet er tomt.
 *
 * A11y: combobox/listbox (piletaster + aria-activedescendant, Enter vaelger, Escape
 * lukker), persistent aria-live annoncerer resultat-antallet, native <dialog>.
 * showModal() giver focus-trap/Escape/inert baggrund; focus-retur til trigger oven paa.
 */
import { fetchSection, swapInner, debounce, abortable, latestBusy, SectionRenderError } from '@theme/section-render';

/** Locale-bevidst rod-URL. Slutter altid paa '/'. (section-render har sin egen, uexporteret.) */
function shopRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

function suggestUrl() {
  return shopRoot() + 'search/suggest';
}

class SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-search-dialog]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.defaultPanel = this.querySelector('[data-search-default]');
    this.defaultLoaded = false;
    this.clearButton = this.querySelector('[data-search-clear]');
    this.live = this.querySelector('[data-search-live]');
    this.errorBox = this.querySelector('[data-search-error]');
    this.opener = null;
    this.activeIndex = -1;
    this.nextSignal = abortable();
    // CUSTOM: r02-m2 - kun det seneste kald rydder busy (aria-busy staar til seneste svar lander).
    this.markBusy = latestBusy((busy) => this.toggleAttribute('aria-busy', busy));

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      this.#setExpanded(false);
      this.#setTriggerExpanded(false);
      /* === CUSTOM: r02-c6-l5 === */
      this.nextSignal(); // afbryd in-flight: intet svar maa repainte en lukket overlay (C6)
      this.#clearResults(); // ryd, saa reopen ALDRIG viser forrige sessions foraeldede liste (L5)
      /* === END CUSTOM: r02-c6-l5 === */
      if (this.opener && document.contains(this.opener)) this.opener.focus();
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.#clear();
        return;
      }
      if (event.target.closest('[data-search-close]')) this.close();
    });
    // To input-lyttere med vilje: skiftet mellem tom tilstand og resultater
    // sker STRAKS (ikke foerst 300ms senere), mens selve soegningen bliver ved
    // med at vente paa debouncen. Symmetrisk i begge retninger: taster man,
    // viger panelet; toemmer man feltet, viger de nu forladte resultater med
    // det samme - ellers stod de to stablet oven paa hinanden i debounce-
    // vinduet (begge er flex: 1 1 auto).
    this.input.addEventListener('input', () => this.#syncPanels());
    this.input.addEventListener(
      'input',
      debounce(() => this.#search(), 300),
    );
    this.input.addEventListener('keydown', this.#onKeydown);

    // B-SEARCH-EXP: giv triggerne en konsistent kollapset starttilstand ved
    // upgrade (markup'et i header.liquid baerer ikke attributten selv, da
    // aria-expanded kun giver mening naar JS har gjort linket til en toggle).
    this.#setTriggerExpanded(false);

    SearchOverlay.instance = this;
    if (!SearchOverlay.documentBound) {
      // Triggeren er et <a href="/search"> (no-JS faldback). Aabn paa CLICK, ikke
      // pointerdown: click er cancelable saa preventDefault stopper navigationen;
      // focus-retur ved luk udloeser ikke click, saa ingen reopen-loop.
      document.addEventListener('click', SearchOverlay.onTriggerPointer);
      document.addEventListener('keydown', SearchOverlay.onTriggerKeydown);
      SearchOverlay.documentBound = true;
    }
  }

  open(opener, seed) {
    if (opener) this.opener = opener;
    if (seed != null) this.input.value = seed;
    if (!this.dialog.open) this.dialog.showModal();
    this.#setTriggerExpanded(true);
    this.input.focus();
    this.input.setSelectionRange(this.input.value.length, this.input.value.length);
    this.#clearError();
    this.#loadDefault();
    this.#syncPanels();
    if (this.input.value.trim()) this.#search();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  async #search() {
    const q = this.input.value.trim();
    if (!q) {
      this.nextSignal(); // CUSTOM: r02-c6 - afbryd in-flight, saa et sent svar ikke repainter en ryddet liste
      this.#clearResults();
      return;
    }
    const doneBusy = this.markBusy(); // CUSTOM: r02-m2
    try {
      const html = await fetchSection('predictive-search', {
        url: suggestUrl(),
        params: { q, 'resources[type]': 'product', 'resources[limit]': 4 },
        signal: this.nextSignal(),
      });
      swapInner(this.results, html, '#shopify-section-predictive-search');
      this.#syncPanels(); // resultaterne findes nu - foerst dér viger den tomme tilstand
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.#setExpanded(this.#options().length > 0);
      this.#announce();
      this.#clearError();
    } catch (error) {
      // Afbrudt kald (supersedet tastetryk) er ikke en fejl.
      if (error.name === 'AbortError') return;
      this.#showError(error);
    } finally {
      doneBusy(); // CUSTOM: r02-m2
    }
  }

  /**
   * Hent den tomme tilstand (populaere soegetermer + bestsellers) ÉN gang ved
   * foerste aabning og behold den. Egen fetch uden nextSignal: den maa ikke
   * afbrydes af et tastetryk, og den skal overleve en luk/aabn-cyklus.
   *
   * Rendres i shop-roden (locale-korrekt) frem for den aktuelle sti, saa et
   * aabent overlay paa en 404-side ikke faar 404 tilbage paa selve panelet.
   * Fejler den, staar panelet bare tomt - det er et tilbud, ikke en funktion,
   * og fejlboksen er reserveret til selve soegningen.
   */
  async #loadDefault() {
    if (!this.defaultPanel || this.defaultLoaded) return;
    this.defaultLoaded = true;
    try {
      const html = await fetchSection('search-default', { url: shopRoot() });
      swapInner(this.defaultPanel, html, '#shopify-section-search-default');
    } catch {
      this.defaultLoaded = false; // proev igen ved naeste aabning
      return;
    }
    // Ingen konfigureret termliste og ingen bestseller-kollektion = sektionen
    // renderer ingen paneler. Smid den tomme node vaek, saa arket ikke aabner
    // med et stykke dead space hvor indholdet skulle have vaeret.
    if (!this.defaultPanel.querySelector('.search-panel__aside, .search-panel__main')) {
      this.defaultPanel.replaceChildren();
      this.defaultPanel.hidden = true;
      this.defaultPanel = null;
      return;
    }
    this.#syncPanels();
  }

  /**
   * Tom tilstand og resultater er gensidigt udelukkende, men skiftet haenger paa
   * RESULTATERNE, ikke paa soegestrengen. Hang det paa strengen, forsvandt den
   * tomme tilstand allerede ved foerste tastetryk, mens svaret var 300ms
   * debounce + en fetch vaek - og panelet klappede sammen til det bare felt
   * for saa at folde sig ud igen med en identisk venstre kolonne. Nu bliver
   * POPULAER SOEGNING + bestsellers staaende, indtil der faktisk ER noget at
   * bytte dem ud med.
   *
   * Tomt felt rydder de nu forladte resultater med det samme (in-flight kald
   * afbrydes, saa et sent svar ikke repainter en ryddet liste).
   */
  #syncPanels() {
    // Ryd-knappen hoerer til feltet, ikke til panelerne, men den skal skifte
    // paa praecis de samme tidspunkter - derfor samme sted. Betingelsen er
    // RAA vaerdi, ikke trim: har man kun tastet mellemrum, er der stadig noget
    // at rydde, selvom der ikke er noget at soege paa.
    if (this.clearButton) this.clearButton.hidden = this.input.value === '';
    if (!this.input.value.trim() && this.results.childNodes.length) {
      this.nextSignal();
      this.#clearResults();
    }
    if (this.defaultPanel) this.defaultPanel.hidden = this.results.childNodes.length > 0;
  }

  /**
   * Ryd feltet UDEN at lukke overlayet - to forskellige handlinger, to
   * forskellige knapper. Fokus foeres tilbage i feltet: knappen skjuler sig
   * selv i #syncPanels, og en skjult knap med fokus efterlader fokus paa
   * <body> midt i en modal dialog.
   *
   * nextSignal() eksplicit foer #syncPanels: rydder man INDEN foerste svar er
   * landet, er this.results stadig tom, og #syncPanels' egen afbrydelse
   * (som haenger paa at der ER resultater) ville lade det svar repainte et
   * felt der nu er tomt.
   */
  #clear() {
    this.input.value = '';
    this.nextSignal();
    this.#clearResults();
    this.#clearError();
    this.#syncPanels();
    this.input.focus();
  }

  #clearResults() {
    this.results.replaceChildren();
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.#setExpanded(false);
    if (this.live) this.live.textContent = '';
  }

  #options() {
    return [...this.results.querySelectorAll('[data-predictive-option]')];
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    const options = this.#options();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!options.length) return;
      event.preventDefault();
      const dir = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + dir + options.length) % options.length;
      this.#highlight(options);
    } else if (event.key === 'Enter') {
      const active = options[this.activeIndex];
      // Med en aktiv mulighed: naviger dertil. Uden: lad formen poste til /search
      // (fuld resultatside) - samme udfald som no-JS.
      if (active && active.dataset.url) {
        event.preventDefault();
        window.location.assign(active.dataset.url);
      }
    }
  };

  #highlight(options) {
    options.forEach((opt, i) => {
      const on = i === this.activeIndex;
      opt.classList.toggle('is-active', on);
      opt.setAttribute('aria-selected', String(on));
    });
    const active = options[this.activeIndex];
    if (active) {
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  #setExpanded(expanded) {
    this.input.setAttribute('aria-expanded', String(expanded));
  }

  // B-SEARCH-EXP: synk aria-expanded paa alle [data-search-trigger] ved open/
  // close (spejler menu-drawer.js' #setExpanded). Header-markup'et ejes af
  // header.liquid og roeres ikke - attributten saettes udelukkende her.
  #setTriggerExpanded(expanded) {
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  // Annoncer resultat-antallet til skaermlaesere. Teksten er server-renderet og
  // localiseret (data-result-announce), saa den er korrekt pr. marked. Den
  // persistente live-region overlever swap (laeses fra den friske node).
  #announce() {
    if (!this.live) return;
    const node = this.results.querySelector('[data-result-announce]');
    const message = node ? node.getAttribute('data-result-announce') : '';
    this.live.textContent = '';
    requestAnimationFrame(() => {
      this.live.textContent = message;
    });
  }

  #showError(error) {
    const message = (error instanceof SectionRenderError && error.message) || error.message || '';
    if (this.errorBox) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
    }
  }

  #clearError() {
    if (this.errorBox) {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }
  }

  static onTriggerPointer(event) {
    const trigger = event.target.closest('[data-search-trigger]');
    if (!trigger) return;
    const overlay = SearchOverlay.instance;
    if (!overlay) return; // ingen enhancement-target -> feltet fungerer som normal form
    event.preventDefault();
    overlay.open(trigger, trigger.value || '');
  }

  static onTriggerKeydown(event) {
    const trigger = event.target.closest('[data-search-trigger]');
    if (!trigger) return;
    const overlay = SearchOverlay.instance;
    if (!overlay) return;
    const key = event.key;
    if (key === 'Tab' || key === 'Shift' || event.metaKey || event.ctrlKey || event.altKey) return;
    // Aabn paa Enter / ArrowDown / et tegn, og overfoer det skrevne til overlayet.
    if (key === 'Enter' || key === 'ArrowDown' || key.length === 1) {
      event.preventDefault();
      let seed = trigger.value || '';
      if (key.length === 1) seed += key;
      overlay.open(trigger, seed);
    }
  }
}

if (!customElements.get('search-overlay')) {
  customElements.define('search-overlay', SearchOverlay);
}
